import { CaretDownIcon, CaretUpIcon } from "@phosphor-icons/react";
import { useT } from "@transifex/react";
import { useState } from "react";
import PhaseSixIcon from "../../../basic/phaseSixIcon/PhaseSixIcon";
import { PhosphorIcon } from "../../../basic/phosphorIcon/PhosphorIcon";
import SortSelect from "../../../basic/sortSelect/SortSelect";
import DottedNameList from "./DottedNameList";
import StudentNameChip from "./StudentNameChip";
import { NameFormats } from "../../../helpers/StudentListImport";

const COLLAPSED_CHIP_COUNT = 6;

interface NameFormatStepProps {
    format: NameFormats;
    /** Title of the selected column, shown as the only format option when a single column is selected. */
    singleColumnTitle: string;
    hasMultipleColumns: boolean;
    namesToAdd: string[];
    skippedExistingCount: number;
    skippedDuplicateNames: string[];
    onChangeFormat: (value: string) => void;
    onRemoveName: (name: string) => void;
}

const NameFormatStep = (props: NameFormatStepProps) => {
    const t = useT();

    const [showAllChips, setShowAllChips] = useState(false);

    const t_nameFormat = t("Format:", { _tags: "SchoolClasses, UploadStudentListPopup" });
    const t_studentsRecognized = (count: number) =>
        count === 1
            ? t("{count} student recognized:", { _tags: "SchoolClasses, UploadStudentListPopup", count })
            : t("{count} students recognized:", { _tags: "SchoolClasses, UploadStudentListPopup", count });
    const t_existingSkipped = (count: number) =>
        count === 1
            ? t("{count} existing student will be skipped", { _tags: "SchoolClasses, UploadStudentListPopup", count })
            : t("{count} existing students will be skipped", { _tags: "SchoolClasses, UploadStudentListPopup", count });
    const t_duplicatesSkipped = (count: number) =>
        count === 1
            ? t("{count} duplicate name will be skipped:", { _tags: "SchoolClasses, UploadStudentListPopup", count })
            : t("{count} duplicate names will be skipped:", { _tags: "SchoolClasses, UploadStudentListPopup", count });
    const t_showMore = t("show more", { _tags: "SchoolClasses, UploadStudentListPopup" });
    const t_showLess = t("show less", { _tags: "SchoolClasses, UploadStudentListPopup" });
    const t_sr_removeChip = t("Remove", { _tags: "SchoolClasses, UploadStudentListPopup, ScreenReaderOnly" });

    const formatLabels: Record<NameFormats, string> = {
        [NameFormats.FIRST_NAME]: t("First name", { _tags: "SchoolClasses, UploadStudentListPopup" }),
        [NameFormats.LAST_NAME]: t("Last name", { _tags: "SchoolClasses, UploadStudentListPopup" }),
        [NameFormats.FIRST_NAME_LAST_NAME]: t("First name Last name", {
            _tags: "SchoolClasses, UploadStudentListPopup",
        }),
        [NameFormats.LAST_NAME_FIRST_NAME]: t("Last name First name", {
            _tags: "SchoolClasses, UploadStudentListPopup",
        }),
        [NameFormats.LAST_NAME_FIRST_INITIAL]: t("Last name F.", { _tags: "SchoolClasses, UploadStudentListPopup" }),
        [NameFormats.FIRST_NAME_LAST_INITIAL]: t("First name L.", { _tags: "SchoolClasses, UploadStudentListPopup" }),
        [NameFormats.FIRST_INITIAL_LAST_INITIAL]: t("F. L.", { _tags: "SchoolClasses, UploadStudentListPopup" }),
        [NameFormats.LAST_INITIAL_FIRST_INITIAL]: t("L. F.", { _tags: "SchoolClasses, UploadStudentListPopup" }),
    };

    const formatOptions = props.hasMultipleColumns
        ? [
              NameFormats.FIRST_NAME,
              NameFormats.LAST_NAME,
              NameFormats.FIRST_NAME_LAST_NAME,
              NameFormats.LAST_NAME_FIRST_NAME,
              NameFormats.LAST_NAME_FIRST_INITIAL,
              NameFormats.FIRST_NAME_LAST_INITIAL,
              NameFormats.FIRST_INITIAL_LAST_INITIAL,
              NameFormats.LAST_INITIAL_FIRST_INITIAL,
          ].map((formatOption) => ({ name: formatLabels[formatOption], value: formatOption }))
        : [{ name: props.singleColumnTitle, value: NameFormats.FIRST_NAME }];

    const renderSkipWarningRow = (text: string) => (
        <div className="flex gap-xs items-center">
            <PhaseSixIcon
                name="alert"
                className="text-xl text-p6-orange shrink-0"
            />
            <span className="text-m text-p6-orange">{text}</span>
        </div>
    );

    const visibleNames = showAllChips ? props.namesToAdd : props.namesToAdd.slice(0, COLLAPSED_CHIP_COUNT);
    const hasHiddenChips = props.namesToAdd.length > COLLAPSED_CHIP_COUNT;

    const hasSkipWarnings = props.skippedExistingCount > 0 || props.skippedDuplicateNames.length > 0;
    // The skip warnings already explain an empty result, so the count line would only repeat them. Without
    // any warning it is the only feedback left, so it stays even at zero.
    const showRecognizedCount = props.namesToAdd.length > 0 || !hasSkipWarnings;

    return (
        <div className="flex flex-col gap-m">
            <div className="flex gap-s items-center">
                <span className="text-l text-p6-grey-dark font-semibold shrink-0">{t_nameFormat}</span>
                <div className="flex-1 min-w-no">
                    <SortSelect
                        options={formatOptions}
                        value={props.format}
                        onChange={props.onChangeFormat}
                    />
                </div>
            </div>
            <div className="flex flex-col gap-xxs">
                <div className="flex flex-col gap-xs">
                    {showRecognizedCount && (
                        <span className="text-l text-p6-grey-dark">
                            {t_studentsRecognized(props.namesToAdd.length)}
                        </span>
                    )}
                    {props.namesToAdd.length > 0 && (
                        <div
                            data-testid="recognized-names-box"
                            className="flex flex-col gap-s border-2 border-p6-grey-lightest rounded-small-container p-s"
                        >
                            <div className="flex flex-wrap gap-s">
                                {visibleNames.map((name) => (
                                    <StudentNameChip
                                        key={name}
                                        name={name}
                                        removeLabel={t_sr_removeChip}
                                        onRemove={() => props.onRemoveName(name)}
                                    />
                                ))}
                            </div>
                            {hasHiddenChips && (
                                <button
                                    type="button"
                                    onClick={() => setShowAllChips(!showAllChips)}
                                    className="flex gap-xs items-center self-start bg-transparent border-none cursor-pointer p-no text-m text-p6-grey-dark hover:text-p6-orange"
                                >
                                    <PhosphorIcon
                                        phosphorIcon={showAllChips ? CaretUpIcon : CaretDownIcon}
                                        fontSize="text-l"
                                    />
                                    {showAllChips ? t_showLess : t_showMore}
                                </button>
                            )}
                        </div>
                    )}
                </div>
                {props.skippedExistingCount > 0 && renderSkipWarningRow(t_existingSkipped(props.skippedExistingCount))}
                {props.skippedDuplicateNames.length > 0 && (
                    <div className="flex flex-col gap-xs">
                        {renderSkipWarningRow(t_duplicatesSkipped(props.skippedDuplicateNames.length))}
                        <DottedNameList
                            names={props.skippedDuplicateNames}
                            textColorClass="text-p6-orange"
                            dotColorClass="bg-p6-orange"
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default NameFormatStep;
